/**
 * Утиліти для CrudResource Feature
 * Містить helper функції для трансформації та обробки даних
 */

import dayjs from 'dayjs'
import React from 'react'
import { App, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { configManager } from '@/Core/Managers/Config'
import { CheckNodeHelperTask } from '@/Core/Tasks/Utils/CheckNodeHelperTask'
import { FileDownloadButton } from './components/FileDownloadButton'
import { restClient } from '@/Core/Services/RestClientService'
/**
 * Інтерфейс для опції статусу
 */
interface StatusOption {
    value: string
    label: string
    color?: string
}

/**
 * Інтерфейс для кольорів статусу
 */
interface StatusColors {
    bgColor: string
    borderColor: string
    textColor: string
}

/**
 * Props для компонента StatusDropdown
 */
interface StatusDropdownProps {
    currentOption: StatusOption
    options: StatusOption[]
    colors: StatusColors
    generateColors: (color: string) => StatusColors
    item: any
    field: any
    crudContext?: any
}

/**
 * Компонент для відображення редагованого статусу з dropdown меню
 */
const StatusDropdown: React.FC<StatusDropdownProps> = ({
    currentOption,
    options,
    colors: initialColors,
    generateColors,
    item,
    field,
    crudContext,
}) => {
    const { notification, modal } = App.useApp()
    const [selectedOption, setSelectedOption] = React.useState<StatusOption>(currentOption)
    const [colors, setColors] = React.useState<StatusColors>(initialColors)
    
    // Формуємо елементи меню для Dropdown
    const menuItems: MenuProps['items'] = options.map((option) => {
        const optionColors = generateColors(option.color || '#8C8C8C')
        return {
            key: option.value,
            style: { padding: '2px 4px' },
            label: React.createElement(
                'span',
                {
                    style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        backgroundColor: optionColors.bgColor,
                        border: `1px solid ${optionColors.borderColor}`,
                        color: optionColors.textColor,
                        borderRadius: '8px',
                        padding: '8px 16px',
                        minHeight: '24px',
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.5',
                        whiteSpace: 'normal',
                        boxSizing: 'border-box',
                    }
                },
                option.label
            ),
            onClick: async () => {
                // Показуємо модальне підтвердження якщо є конфіг
                if (
                    Array.isArray(field?.confirmOnValues) &&
                    field.confirmOnValues.includes(option.value) &&
                    field?.confirmText
                ) {
                    const confirmed = await new Promise<boolean>((resolve) => {
                        modal.confirm({
                            title: 'Підтвердження',
                            content: field.confirmText,
                            okText: 'Так',
                            cancelText: 'Скасувати',
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        })
                    })
                    if (!confirmed) return
                }

                const itemIdRaw = item['@id']
                if (!itemIdRaw || typeof itemIdRaw !== 'string') {
                    notification.error({
                        duration: 5,
                        title: 'Помилка',
                        description: 'Не вдалося визначити ідентифікатор запису',
                        placement: 'bottomRight',
                    })
                    return
                }
                const itemId: string = itemIdRaw
                const changeUrl = typeof field?.getChangeUrl === 'function' ? field.getChangeUrl({
                    currentOption,
                    options,
                    colors: initialColors,
                    generateColors,
                    item,
                    field
                }) : itemId 
                const patchPromise = restClient.patch(changeUrl, {
                    json: {
                        status: option.value,
                    }
                })
                
                if (!patchPromise) {
                    notification.error({
                        duration: 5,
                        title: 'Помилка',
                        description: 'Не вдалося виконати запит',
                        placement: 'bottomRight',
                    })
                    return
                }
                
                patchPromise
                .then(() => {
                    // Оновлюємо локальний стан після успішного запиту
                    setSelectedOption(option)
                    setColors(generateColors(option.color || '#8C8C8C'))
                    
                    notification.success({
                        duration: 3,
                        title: 'Успішно',
                        description: `Статус змінено на "${option.label}"`,
                        placement: 'bottomRight',
                    })

                    // Викликаємо onAfterChange якщо є конфіг
                    if (typeof field?.onAfterChange === 'function') {
                        field.onAfterChange({ option, item, crudContext })
                    }
                })
                .catch((error: any) => {
                    notification.error({
                        duration: 5,
                        title: 'Помилка',
                        description: error?.message || 'Не вдалося змінити статус',
                        placement: 'bottomRight',
                    })
                })
            }
        }
    })

    // Іконка стрілки вниз
    const ArrowIcon = React.createElement(
        'svg',
        {
            width: '8',
            height: '5',
            viewBox: '0 0 8 5',
            fill: 'none',
            xmlns: 'http://www.w3.org/2000/svg',
            style: { marginLeft: '8px', flexShrink: 0 }
        },
        React.createElement('path', {
            d: 'M3.91992 4.5957C3.67246 4.58382 3.40123 4.4627 3.18164 4.24316L0.353516 1.41504C-0.0370083 1.02451 -0.116136 0.469651 0.176758 0.176758C0.469756 -0.115562 1.02375 -0.0367379 1.41406 0.353516L4 2.93945L6.58496 0.353516C6.9754 -0.0366158 7.52943 -0.115862 7.82227 0.176758C8.11516 0.469651 8.03603 1.02451 7.64551 1.41504L4.81738 4.24316C4.59767 4.46288 4.32573 4.58392 4.07812 4.5957H3.91992Z',
            fill: colors.textColor
        })
    )

    return React.createElement(
        Dropdown,
        {
            menu: { items: menuItems },
            trigger: ['click'] as const
        },
        React.createElement(
            'span',
            {
                className: 'status-badge-table status-badge-editable',
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    backgroundColor: colors.bgColor,
                    color: colors.textColor,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 400,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                }
            },
            selectedOption.label,
            ArrowIcon
        )
    )
}

/**
 * Параметри для функції transformFieldValue
 */
export interface TransformFieldValueParams {
    /** Конфігурація поля з resource.fields */
    field: any
    /** Значення поля для трансформації */
    value: any
    /** Контекст layout */
    defaultLayoutContext?: any
    /** Повний об'єкт запису (використовується для editorjs та інших складних полів) */
    item: any
    /** Контекст CRUD ресурсу */
    crudContext?: any
    /** Контекст таблиці */
    tableContext?: any
}

/**
 * Трансформує значення поля для відображення в таблиці
 * Обробляє різні типи полів: дати, switch, color, editorjs, select тощо
 * 
 * @param params - об'єкт з параметрами
 * @param params.field - конфігурація поля з resource.fields
 * @param params.value - значення поля для трансформації
 * @param params.defaultLayoutContext - контекст layout
 * @param params.item - повний об'єкт запису (використовується для editorjs та інших складних полів)
 * @param params.crudContext - контекст CRUD ресурсу
 * @param params.tableContext - контекст таблиці
 * @returns трансформоване значення (може бути string, React.ReactElement або інше)
 */
export const transformFieldValue = ({
    field,
    value,
    defaultLayoutContext: _defaultLayoutContext,
    item,
    crudContext: _crudContext,
    tableContext: _tableContext,
}: TransformFieldValueParams): any => {
    let dateObj = null
    const AppConfig = configManager.get('AppConfig') as any
    
    switch (field.type) {
        case 'date':
            dateObj = dayjs(value)
            value = dateObj.format(AppConfig?.dateDefaultFormat ?? 'DD.MM.YYYY')
            break
            
        case 'datetime':
            dateObj = dayjs(value)
            value = dateObj.format(AppConfig?.datetimeDefaultFormat ?? 'DD.MM.YYYY HH:mm')
            break
            
        case 'switch':
            value = value === true ? 'Так' : 'Ні'
            break
            
        case 'color':
            if (value.indexOf('#') !== -1)
                value = React.createElement(
                    'div',
                    { style: { padding: 1, border: '1px solid #d9d9d9', borderRadius: 8, width: 29, height: 29 } },
                    React.createElement('span', {
                        style: { display: 'block', backgroundColor: value, width: 25, height: 25, borderRadius: 8 },
                    })
                )
            break
            
        case 'file':
            // Обробка файлів - показуємо кнопку скачування
            if (typeof value === 'object' && value !== null) {
                const fileUrl = value.contentUrl || value.url
                const fileName = value.originalName || value.name || 'Файл'
                
                if (fileUrl) {
                    value = React.createElement(FileDownloadButton, {
                        fileUrl,
                        fileName,
                    })
                } else {
                    value = fileName
                }
            }
            break
            
        case 'editorjs':
            if (field?.fieldHTMLName) {
                const htmlField = item[field.fieldHTMLName]
                if (htmlField) value = React.createElement('div', { dangerouslySetInnerHTML: { __html: htmlField } })
            }
            break
            
        case 'status':
            if (typeof value === 'string' && Array.isArray(field.options)) {
                const currentOption = field.options.find((opt: any) => opt.value === value)

                if (currentOption) {
                    // Функція для генерації кольорів під темну тему.
                    // Використовуємо rgba з низькою opacity замість змішування з білим,
                    // щоб фон був коректним і на темному, і на світлому тлі.
                    const generateColors = (color: string) => {
                        const hexToRgb = (hex: string) => {
                            let cleanHex = hex.replace(/^#/, '').substring(0, 6)
                            if (cleanHex.length === 3) {
                                cleanHex = cleanHex.split('').map((c: string) => c + c).join('')
                            }
                            const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex)
                            return result ? {
                                r: parseInt(result[1]!, 16),
                                g: parseInt(result[2]!, 16),
                                b: parseInt(result[3]!, 16)
                            } : { r: 140, g: 140, b: 140 }
                        }

                        const rgb = hexToRgb(color)
                        const bgColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.85)`
                        const borderColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`
                        
                        return { bgColor, borderColor, textColor: '#ffffff' }
                    }

                    const colors = generateColors(currentOption.color || '#8C8C8C')
                    
                    // Перевіряємо чи є доступ до редагування
                    const hasAccess = _crudContext?.accessFn({
                        accessMode: 'update_status',
                        accessItem: item,
                        crudContext: _crudContext,
                    }) === true && field.inputProps?.disabled !== true
                    
                    if (hasAccess) {
                        // Створюємо редагований статус з dropdown
                        value = React.createElement(StatusDropdown, {
                            currentOption,
                            options: field.options,
                            colors,
                            generateColors,
                            item,
                            field,
                            crudContext: _crudContext,
                        })
                    } else {
                        // Статичний бейдж без можливості редагування
                        value = React.createElement(
                            'span',
                            {
                                className: 'status-badge-table',
                                style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    width: '100%',
                                    backgroundColor: colors.bgColor,
                                    color: colors.textColor,
                                    border: `1px solid ${colors.borderColor}`,
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    boxSizing: 'border-box',
                                }
                            },
                            currentOption.label
                        )
                    }
                }
            }
            break
            
        default:
            break
    }

    // Обробка select з опціями
    if (field.type === 'select' && typeof value === 'string' && Array.isArray(field.options)) {
        field.options.forEach((option: any) => {
            if (option.value == value) value = option.label
        })
    }

    // Обробка select з ресурсом та лінком
    if (
        field.type === 'select' &&
        typeof field.resource === 'string' &&
        typeof value === 'object' &&
        field?.isLink === true &&
        !Array.isArray(value)
    ) {
        let linkTitle = ''
        if (value?.fullName !== undefined) linkTitle = value.fullName
        else if (value?.lastName !== undefined) linkTitle = `${value.lastName ?? ''} ${value.firstName ?? ''}`
        else if (value?.title !== undefined) linkTitle = value.title
        else if (value?.name !== undefined) linkTitle = value.name
        else linkTitle = '-'

        // TODO: Реалізувати відкриття ресурсу через defaultLayoutContext
        // Потребує інтеграції з ResourceStore та роутингом
        return linkTitle
    }

    // Обробка об'єктів
    if (typeof value === 'object' && !CheckNodeHelperTask.isElement(value)) {
        if (value?.fullName !== undefined) value = value.fullName
        else if (value?.lastName !== undefined) value = `${value.lastName ?? ''} ${value.firstName ?? ''}`
        else if (value?.title !== undefined) value = value.title
        else if (value?.name !== undefined) value = value.name
        else value = '-'
    }

    return value
}
